import React from 'react'
import {
  AerospaceDamageId,
  KestrelInspectionType,
  KVAerospaceAnnotation,
  KVAnnotation,
  KVNavyAnnotation,
  KVRooftopAnnotation,
  RooftopDamageId,
} from 'types'

import { AerospaceDamageSelector } from './AerospaceDamageSelector'
import { G1N6DamageSelector } from './G1N6DamageSelector'
import { NavyDamageSelector } from './NavyDamageSelector'
import { getAllNavyDamageTypes } from './navyDamageTypes'
import { RooftopDamageSelector } from './RooftopDamageSelector'
import { DamageTypeHandler } from './types'

export const navyDamageHandler: DamageTypeHandler = {
  inspectionType: KestrelInspectionType.NAVY,

  getInitialSelection: (annotation: KVAnnotation): string[] => {
    if (!annotation.navyAnnotation) {
      return []
    }

    const selected: string[] = []
    getAllNavyDamageTypes().forEach((damageType) => {
      if (
        annotation.navyAnnotation?.[
          damageType.navyProperty as keyof KVNavyAnnotation
        ]
      ) {
        selected.push(damageType.id)
      }
    })

    return selected
  },

  updateAnnotation: (
    annotation: KVAnnotation,
    selectedTypes: string[],
    setAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): void => {
    const newNavyAnnotation: KVNavyAnnotation =
      annotation.navyAnnotation || new KVNavyAnnotation()

    getAllNavyDamageTypes().forEach((damageType) => {
      if (selectedTypes.includes(damageType.id)) {
        type K = typeof damageType.navyProperty
        type V = K extends keyof KVNavyAnnotation ? KVNavyAnnotation[K] : never
        ;(newNavyAnnotation[
          damageType.navyProperty as keyof KVNavyAnnotation
        ] as V) = true as unknown as V
      } else {
        type K = typeof damageType.navyProperty
        type V = K extends keyof KVNavyAnnotation ? KVNavyAnnotation[K] : never
        ;(newNavyAnnotation[
          damageType.navyProperty as keyof KVNavyAnnotation
        ] as V) = false as unknown as V
      }
    })

    setAnnotation((prev) => ({
      ...prev,
      navyAnnotation: newNavyAnnotation,
    }))
  },

  renderSelector: (
    selectedTypes: string[],
    onSelectionChange: (types: string[]) => void,
    annotation: KVAnnotation,
    setAnnotation?: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): React.ReactNode => {
    if (!setAnnotation) return null
    return (
      <NavyDamageSelector
        selectedTypes={selectedTypes}
        onSelectionChange={onSelectionChange}
        annotation={annotation}
        setAnnotation={setAnnotation}
      />
    )
  },
}

export const rooftopDamageHandler: DamageTypeHandler = {
  inspectionType: KestrelInspectionType.ROOFTOP,

  getInitialSelection: (annotation: KVAnnotation): string[] => {
    if (!annotation.rooftopAnnotation) {
      return []
    }

    if (annotation.rooftopAnnotation.selected?.length) {
      return annotation.rooftopAnnotation.selected
    }

    return []
  },

  updateAnnotation: (
    annotation: KVAnnotation,
    selectedTypes: string[],
    setAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): void => {
    const newRooftopAnnotation: KVRooftopAnnotation =
      annotation.rooftopAnnotation || new KVRooftopAnnotation()

    const validIds = new Set(Object.values(RooftopDamageId))
    newRooftopAnnotation.selected = selectedTypes.filter(
      (id): id is RooftopDamageId => validIds.has(id as RooftopDamageId)
    )

    setAnnotation((prev) => ({
      ...prev,
      rooftopAnnotation: newRooftopAnnotation,
    }))
  },

  renderSelector: (
    selectedTypes: string[],
    onSelectionChange: (types: string[]) => void,
    annotation: KVAnnotation
  ): React.ReactNode => {
    return (
      <RooftopDamageSelector
        selectedTypes={selectedTypes}
        onSelectionChange={onSelectionChange}
        annotation={annotation}
      />
    )
  },
}

export const aerospaceDamageHandler: DamageTypeHandler = {
  inspectionType: KestrelInspectionType.AEROSPACE,

  getInitialSelection: (annotation: KVAnnotation): string[] => {
    if (!annotation.aerospaceAnnotation) {
      return []
    }
    return annotation.aerospaceAnnotation.selected || []
  },

  updateAnnotation: (
    annotation: KVAnnotation,
    selectedTypes: string[],
    setAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): void => {
    const newAerospaceAnnotation: KVAerospaceAnnotation =
      annotation.aerospaceAnnotation || new KVAerospaceAnnotation()

    const validIds = new Set(Object.values(AerospaceDamageId))
    newAerospaceAnnotation.selected = selectedTypes.filter(
      (id): id is AerospaceDamageId => validIds.has(id as AerospaceDamageId)
    )

    setAnnotation((prev) => ({
      ...prev,
      aerospaceAnnotation: newAerospaceAnnotation,
    }))
  },

  renderSelector: (
    selectedTypes: string[],
    onSelectionChange: (types: string[]) => void,
    annotation: KVAnnotation
  ): React.ReactNode => {
    return (
      <AerospaceDamageSelector
        selectedTypes={selectedTypes}
        onSelectionChange={onSelectionChange}
        annotation={annotation}
      />
    )
  },
}

export const g1n6DamageHandler: DamageTypeHandler = {
  inspectionType: KestrelInspectionType.G1N6,

  getInitialSelection: (): string[] => {
    return []
  },

  updateAnnotation: (): void => {
    // G1N6 damage selector manages its own state directly
  },

  renderSelector: (
    _selectedTypes: string[],
    _onSelectionChange: (types: string[]) => void,
    annotation: KVAnnotation,
    setAnnotation?: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): React.ReactNode => {
    if (!setAnnotation) return null
    return (
      <G1N6DamageSelector
        annotation={annotation}
        setAnnotation={setAnnotation}
      />
    )
  },
}

// Registry of all damage type handlers
export const damageTypeHandlers = new Map<
  KestrelInspectionType,
  DamageTypeHandler
>([
  [KestrelInspectionType.NAVY, navyDamageHandler],
  [KestrelInspectionType.AEROSPACE, aerospaceDamageHandler],
  [KestrelInspectionType.ROOFTOP, rooftopDamageHandler],
  [KestrelInspectionType.G1N6, g1n6DamageHandler],
])

export const getDamageTypeHandler = (
  inspectionType: KestrelInspectionType
): DamageTypeHandler => {
  const handler = damageTypeHandlers.get(inspectionType)
  if (!handler) {
    return navyDamageHandler
  }
  return handler
}
